import React, { useState, useEffect, useMemo } from 'react';
import { 
  Wallet, TrendingUp, TrendingDown, Clock, Package, 
  CreditCard, ArrowUpRight, ArrowDownRight, RefreshCw,
  ChevronDown, ChevronRight, AlertTriangle, CheckCircle,
  Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { formatCurrency, cn } from '../lib/utils';

// Months for FY (Apr-Mar)
const MONTHS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];

// Generate mock working capital data
const generateWorkingCapitalData = () => {
  const data = {
    monthly: [],
    current: {},
    components: {
      currentAssets: {},
      currentLiabilities: {}
    }
  };

  let cumulativeCash = 15000000; // 1.5 Cr starting cash
  
  for (let i = 0; i < 12; i++) {
    // Seasonal patterns
    const seasonalMult = i >= 6 && i <= 8 ? 1.4 : i >= 9 ? 1.1 : 1.0;
    
    // Revenue for the month
    const revenue = 8500000 * seasonalMult * (1 + i * 0.02);
    const cogs = revenue * 0.35;
    
    // Working capital components
    const receivables = revenue * 0.15 * (1 + Math.random() * 0.1); // ~15% of revenue
    const inventory = cogs * 2.5 * (1 + Math.random() * 0.15); // ~2.5 months of COGS
    const prepaidExpenses = 800000 + Math.random() * 200000;
    const advances = 500000 + Math.random() * 200000;
    
    const payables = cogs * 0.8 * (1 + Math.random() * 0.1); // ~80% of monthly COGS
    const accruedExpenses = 1200000 + Math.random() * 300000;
    const shortTermDebt = 5000000;
    const statutoryDues = 400000 + Math.random() * 100000;
    
    // Cash flow effect
    const operatingCashFlow = revenue * 0.08 - (Math.random() * 500000);
    cumulativeCash += operatingCashFlow;
    
    // Calculate metrics
    const dso = Math.round((receivables / revenue) * 30); // Days
    const dio = Math.round((inventory / (cogs / 30))); // Days
    const dpo = Math.round((payables / (cogs / 30))); // Days
    const ccc = dso + dio - dpo; // Cash Conversion Cycle
    
    const currentAssets = cumulativeCash + receivables + inventory + prepaidExpenses + advances;
    const currentLiabilities = payables + accruedExpenses + shortTermDebt + statutoryDues;
    const netWorkingCapital = currentAssets - currentLiabilities;
    const workingCapitalRatio = currentAssets / currentLiabilities;
    const quickRatio = (currentAssets - inventory) / currentLiabilities;
    
    data.monthly.push({
      month: MONTHS[i],
      revenue,
      cogs,
      // Key metrics
      dso,
      dio,
      dpo,
      ccc,
      // Balance
      currentAssets,
      currentLiabilities,
      netWorkingCapital,
      workingCapitalRatio,
      quickRatio,
      // Components - Assets
      cash: cumulativeCash,
      receivables,
      inventory,
      prepaidExpenses,
      advances,
      // Components - Liabilities
      payables,
      accruedExpenses,
      shortTermDebt,
      statutoryDues,
    });
  }
  
  // Current period (latest month)
  data.current = data.monthly[data.monthly.length - 1];
  
  // Component breakdowns for current period
  data.components.currentAssets = {
    cash: { value: data.current.cash, label: 'Cash & Bank', icon: Wallet },
    receivables: { value: data.current.receivables, label: 'Trade Receivables', icon: CreditCard },
    inventory: { value: data.current.inventory, label: 'Inventory', icon: Package },
    prepaidExpenses: { value: data.current.prepaidExpenses, label: 'Prepaid Expenses', icon: Clock },
    advances: { value: data.current.advances, label: 'Advances', icon: ArrowUpRight },
  };
  
  data.components.currentLiabilities = {
    payables: { value: data.current.payables, label: 'Trade Payables', icon: CreditCard },
    accruedExpenses: { value: data.current.accruedExpenses, label: 'Accrued Expenses', icon: Clock },
    shortTermDebt: { value: data.current.shortTermDebt, label: 'Short-term Debt', icon: Wallet },
    statutoryDues: { value: data.current.statutoryDues, label: 'Statutory Dues', icon: AlertTriangle },
  };
  
  // Receivables by channel
  data.receivablesByChannel = [
    { channel: 'D2C Website', amount: data.current.receivables * 0.25, days: 3 },
    { channel: 'Amazon', amount: data.current.receivables * 0.18, days: 14 },
    { channel: 'Flipkart', amount: data.current.receivables * 0.15, days: 21 },
    { channel: 'Myntra', amount: data.current.receivables * 0.12, days: 30 },
    { channel: 'Blinkit', amount: data.current.receivables * 0.10, days: 7 },
    { channel: 'Zepto', amount: data.current.receivables * 0.08, days: 7 },
    { channel: 'Other', amount: data.current.receivables * 0.12, days: 45 },
  ];
  
  // Inventory by category
  data.inventoryByCategory = [
    { category: 'Jewellery', amount: data.current.inventory * 0.30, days: 75 },
    { category: 'Watches', amount: data.current.inventory * 0.18, days: 90 },
    { category: 'Sunglasses', amount: data.current.inventory * 0.15, days: 60 },
    { category: 'Bag Charms', amount: data.current.inventory * 0.10, days: 45 },
    { category: 'Hair Accessories', amount: data.current.inventory * 0.12, days: 50 },
    { category: 'Others', amount: data.current.inventory * 0.15, days: 55 },
  ];
  
  // Payables by vendor type
  data.payablesByVendor = [
    { vendor: 'Raw Material Suppliers', amount: data.current.payables * 0.40, days: 45 },
    { vendor: 'Packaging Vendors', amount: data.current.payables * 0.15, days: 30 },
    { vendor: 'Logistics Partners', amount: data.current.payables * 0.20, days: 15 },
    { vendor: 'Marketing Agencies', amount: data.current.payables * 0.15, days: 30 },
    { vendor: 'Technology Vendors', amount: data.current.payables * 0.10, days: 45 },
  ];
  
  return data;
};

// KPI Card Component
const KPICard = ({ title, value, unit, trend, trendValue, icon: Icon, color = 'primary', tooltip }) => {
  const colorClasses = {
    primary: 'bg-primary-50 border-primary-200 text-primary-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
  };

  return (
    <div className={cn('rounded-xl border p-4', colorClasses[color])} data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 opacity-70" />}
          <span className="text-xs font-medium uppercase tracking-wide opacity-80">{title}</span>
        </div>
        {tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info className="w-3.5 h-3.5 opacity-50 hover:opacity-100" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs text-xs">{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <div className="flex items-end gap-2">
        <span className="font-mono text-2xl font-bold">{value}</span>
        {unit && <span className="text-sm opacity-70 mb-0.5">{unit}</span>}
      </div>
      {trend !== undefined && (
        <div className={cn('flex items-center gap-1 mt-1 text-xs', trend >= 0 ? 'text-emerald-600' : 'text-red-600')}>
          {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          <span>{trend >= 0 ? '+' : ''}{trendValue}</span>
          <span className="opacity-60">vs last month</span>
        </div>
      )}
    </div>
  );
};

// Health indicator component
const HealthIndicator = ({ value, thresholds, inverse = false }) => {
  let status = 'good';
  let color = 'emerald';
  
  if (inverse) {
    // Lower is better (e.g., DSO, DIO, CCC)
    if (value > thresholds.critical) {
      status = 'critical';
      color = 'red';
    } else if (value > thresholds.warning) {
      status = 'warning';
      color = 'amber';
    }
  } else {
    // Higher is better (e.g., ratio, DPO)
    if (value < thresholds.critical) {
      status = 'critical';
      color = 'red';
    } else if (value < thresholds.warning) {
      status = 'warning';
      color = 'amber';
    }
  }
  
  return (
    <span className={cn(
      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
      color === 'emerald' && 'bg-emerald-100 text-emerald-700',
      color === 'amber' && 'bg-amber-100 text-amber-700',
      color === 'red' && 'bg-red-100 text-red-700'
    )}>
      {status === 'good' && <CheckCircle className="w-3 h-3" />}
      {status === 'warning' && <AlertTriangle className="w-3 h-3" />}
      {status === 'critical' && <AlertTriangle className="w-3 h-3" />}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

export default function WorkingCapitalPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [expandedSections, setExpandedSections] = useState(new Set(['assets', 'liabilities']));

  useEffect(() => {
    setLoading(true);
    // Simulate data fetch
    setTimeout(() => {
      setData(generateWorkingCapitalData());
      setLoading(false);
    }, 500);
  }, []);

  const toggleSection = (section) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  // Calculate trends
  const trends = useMemo(() => {
    if (!data || data.monthly.length < 2) return {};
    const current = data.monthly[data.monthly.length - 1];
    const previous = data.monthly[data.monthly.length - 2];
    return {
      dso: current.dso - previous.dso,
      dio: current.dio - previous.dio,
      dpo: current.dpo - previous.dpo,
      ccc: current.ccc - previous.ccc,
      nwc: ((current.netWorkingCapital - previous.netWorkingCapital) / previous.netWorkingCapital * 100),
      ratio: current.workingCapitalRatio - previous.workingCapitalRatio,
    };
  }, [data]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-slate-200 rounded w-64 animate-pulse" />
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-28 bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
        <div className="h-96 bg-slate-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  const current = data.current;

  return (
    <div className="space-y-6" data-testid="working-capital-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Working Capital</h2>
          <p className="text-slate-500">Monitor cash conversion cycle and liquidity metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current">Current</SelectItem>
              <SelectItem value="q1">Q1 FY25</SelectItem>
              <SelectItem value="q2">Q2 FY25</SelectItem>
              <SelectItem value="q3">Q3 FY25</SelectItem>
              <SelectItem value="q4">Q4 FY25</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <KPICard
          title="DSO"
          value={current.dso}
          unit="days"
          icon={Clock}
          color={current.dso > 30 ? 'amber' : 'emerald'}
          trend={trends.dso}
          trendValue={`${Math.abs(trends.dso)} days`}
          tooltip="Days Sales Outstanding - Average time to collect receivables"
        />
        <KPICard
          title="DIO"
          value={current.dio}
          unit="days"
          icon={Package}
          color={current.dio > 60 ? 'amber' : 'emerald'}
          trend={trends.dio}
          trendValue={`${Math.abs(trends.dio)} days`}
          tooltip="Days Inventory Outstanding - Average time inventory is held"
        />
        <KPICard
          title="DPO"
          value={current.dpo}
          unit="days"
          icon={CreditCard}
          color={current.dpo < 30 ? 'amber' : 'emerald'}
          trend={trends.dpo}
          trendValue={`${Math.abs(trends.dpo)} days`}
          tooltip="Days Payable Outstanding - Average time to pay suppliers"
        />
        <KPICard
          title="CCC"
          value={current.ccc}
          unit="days"
          icon={RefreshCw}
          color={current.ccc > 60 ? 'red' : current.ccc > 40 ? 'amber' : 'emerald'}
          trend={trends.ccc}
          trendValue={`${Math.abs(trends.ccc)} days`}
          tooltip="Cash Conversion Cycle (DSO + DIO - DPO) - Days to convert inventory to cash"
        />
        <KPICard
          title="Current Ratio"
          value={current.workingCapitalRatio.toFixed(2)}
          unit="x"
          icon={Wallet}
          color={current.workingCapitalRatio < 1.2 ? 'red' : current.workingCapitalRatio < 1.5 ? 'amber' : 'emerald'}
          tooltip="Current Assets / Current Liabilities - Liquidity measure"
        />
        <KPICard
          title="Quick Ratio"
          value={current.quickRatio.toFixed(2)}
          unit="x"
          icon={Wallet}
          color={current.quickRatio < 0.8 ? 'red' : current.quickRatio < 1.0 ? 'amber' : 'emerald'}
          tooltip="(Current Assets - Inventory) / Current Liabilities - Acid test"
        />
      </div>

      {/* Net Working Capital Summary */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-primary-200 text-sm">Net Working Capital</p>
            <p className="font-mono text-3xl font-bold mt-1">
              {formatCurrency(current.netWorkingCapital, { compact: true })}
            </p>
            <div className="flex items-center gap-4 mt-2 text-sm">
              <span className="text-primary-200">
                Current Assets: {formatCurrency(current.currentAssets, { compact: true })}
              </span>
              <span className="text-primary-300">−</span>
              <span className="text-primary-200">
                Current Liabilities: {formatCurrency(current.currentLiabilities, { compact: true })}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className={cn(
              'inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium',
              trends.nwc >= 0 ? 'bg-emerald-500/20 text-emerald-100' : 'bg-red-500/20 text-red-100'
            )}>
              {trends.nwc >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              {trends.nwc >= 0 ? '+' : ''}{trends.nwc.toFixed(1)}% MoM
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current Assets Breakdown */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleSection('assets')}
            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              {expandedSections.has('assets') ? (
                <ChevronDown className="w-5 h-5 text-slate-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-slate-400" />
              )}
              <span className="font-heading font-semibold text-slate-900">Current Assets</span>
            </div>
            <span className="font-mono font-bold text-emerald-600">
              {formatCurrency(current.currentAssets, { compact: true })}
            </span>
          </button>
          
          {expandedSections.has('assets') && (
            <div className="px-4 pb-4 space-y-2">
              {Object.entries(data.components.currentAssets).map(([key, item]) => (
                <div key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <item.icon className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="text-slate-700">{item.label}</span>
                  </div>
                  <span className="font-mono font-medium text-slate-900">
                    {formatCurrency(item.value, { compact: true })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Current Liabilities Breakdown */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleSection('liabilities')}
            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              {expandedSections.has('liabilities') ? (
                <ChevronDown className="w-5 h-5 text-slate-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-slate-400" />
              )}
              <span className="font-heading font-semibold text-slate-900">Current Liabilities</span>
            </div>
            <span className="font-mono font-bold text-red-600">
              {formatCurrency(current.currentLiabilities, { compact: true })}
            </span>
          </button>
          
          {expandedSections.has('liabilities') && (
            <div className="px-4 pb-4 space-y-2">
              {Object.entries(data.components.currentLiabilities).map(([key, item]) => (
                <div key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                      <item.icon className="w-4 h-4 text-red-600" />
                    </div>
                    <span className="text-slate-700">{item.label}</span>
                  </div>
                  <span className="font-mono font-medium text-slate-900">
                    {formatCurrency(item.value, { compact: true })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detailed Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Receivables by Channel */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">Receivables by Channel</h3>
          <div className="space-y-3">
            {data.receivablesByChannel.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary-500" />
                  <span className="text-sm text-slate-600">{item.channel}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400">{item.days}d</span>
                  <span className="font-mono text-sm font-medium text-slate-900">
                    {formatCurrency(item.amount, { compact: true })}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between">
            <span className="text-sm font-medium text-slate-600">Weighted Avg DSO</span>
            <div className="flex items-center gap-2">
              <span className="font-mono font-bold text-slate-900">{current.dso} days</span>
              <HealthIndicator value={current.dso} thresholds={{ warning: 25, critical: 35 }} inverse />
            </div>
          </div>
        </div>

        {/* Inventory by Category */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">Inventory by Category</h3>
          <div className="space-y-3">
            {data.inventoryByCategory.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-amber-500" />
                  <span className="text-sm text-slate-600">{item.category}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400">{item.days}d</span>
                  <span className="font-mono text-sm font-medium text-slate-900">
                    {formatCurrency(item.amount, { compact: true })}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between">
            <span className="text-sm font-medium text-slate-600">Weighted Avg DIO</span>
            <div className="flex items-center gap-2">
              <span className="font-mono font-bold text-slate-900">{current.dio} days</span>
              <HealthIndicator value={current.dio} thresholds={{ warning: 50, critical: 70 }} inverse />
            </div>
          </div>
        </div>

        {/* Payables by Vendor */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">Payables by Vendor Type</h3>
          <div className="space-y-3">
            {data.payablesByVendor.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <span className="text-sm text-slate-600">{item.vendor}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400">{item.days}d</span>
                  <span className="font-mono text-sm font-medium text-slate-900">
                    {formatCurrency(item.amount, { compact: true })}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between">
            <span className="text-sm font-medium text-slate-600">Weighted Avg DPO</span>
            <div className="flex items-center gap-2">
              <span className="font-mono font-bold text-slate-900">{current.dpo} days</span>
              <HealthIndicator value={current.dpo} thresholds={{ warning: 35, critical: 25 }} />
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Trend Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h3 className="font-heading font-semibold text-slate-900">Monthly Working Capital Trend</h3>
        </div>
        <ScrollArea className="max-h-96">
          <table className="w-full">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Month</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">DSO</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">DIO</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">DPO</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">CCC</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">NWC</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Ratio</th>
              </tr>
            </thead>
            <tbody>
              {data.monthly.map((month, index) => (
                <tr key={index} className={cn('border-b border-slate-50', index === data.monthly.length - 1 && 'bg-primary-50')}>
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{month.month}</td>
                  <td className="px-4 py-3 text-sm text-right font-mono">
                    <span className={cn(month.dso > 30 ? 'text-amber-600' : 'text-slate-700')}>{month.dso}d</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-mono">
                    <span className={cn(month.dio > 60 ? 'text-amber-600' : 'text-slate-700')}>{month.dio}d</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-mono">
                    <span className={cn(month.dpo < 30 ? 'text-amber-600' : 'text-slate-700')}>{month.dpo}d</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-mono">
                    <span className={cn(
                      month.ccc > 60 ? 'text-red-600' : month.ccc > 40 ? 'text-amber-600' : 'text-emerald-600'
                    )}>{month.ccc}d</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-mono font-medium">
                    {formatCurrency(month.netWorkingCapital, { compact: true })}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-mono">
                    <span className={cn(
                      month.workingCapitalRatio < 1.2 ? 'text-red-600' : month.workingCapitalRatio < 1.5 ? 'text-amber-600' : 'text-emerald-600'
                    )}>{month.workingCapitalRatio.toFixed(2)}x</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollArea>
      </div>

      {/* Insights */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-900">Working Capital Insights</h4>
            <ul className="mt-2 text-sm text-amber-800 space-y-1">
              <li>• Cash Conversion Cycle of {current.ccc} days indicates moderate efficiency</li>
              <li>• Consider negotiating better payment terms with suppliers to increase DPO</li>
              <li>• Inventory holding for {current.dio} days - review slow-moving SKUs</li>
              {current.dso > 25 && <li>• DSO above 25 days - accelerate receivables collection from marketplaces</li>}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
